<template>
  <Teleport to="body">
    <ZoyaBubble v-if="pos" ref="bubbleRef" class="chart-day-bubble" :placement="pos.placement"
                :style="{ left: pos.left + 'px', top: pos.top + 'px', maxWidth: pos.maxWidth + 'px', '--tail-x': pos.tailX + 'px' }"
                role="dialog" aria-label="Подробности дня" aria-live="polite">
      <button class="close" aria-label="Закрыть подробности дня" @click="$emit('close')">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
      <p class="date">{{ label }}</p>
      <p class="value">{{ value }}</p>
      <div class="actions">
        <button class="action" @click="$emit('open')"><span class="btn-pill">{{ action }}</span></button>
      </div>
    </ZoyaBubble>
  </Teleport>
</template>

<script setup>
import { ref, nextTick, watch, onMounted, onUnmounted } from 'vue'
import ZoyaBubble from './ZoyaBubble.vue'
const props = defineProps({ anchor: { type: Object, required: true }, label: String, value: String, action: { type: String, default: 'Открыть день' } })
const emit = defineEmits(['close', 'open'])
const bubbleRef = ref(null)
const pos = ref(null)
let raf = 0
function tick() {
  const anchor = props.anchor
  if (!anchor?.isConnected) { emit('close'); return }
  const r = anchor.getBoundingClientRect()
  const vw = window.innerWidth, vh = window.visualViewport?.height || window.innerHeight
  if (r.bottom < 90 || r.top > vh - 80 || r.right < 0 || r.left > vw) { emit('close'); return }
  const column = Math.min(vw, 430), edge = (vw - column) / 2 + 20
  const maxWidth = column - 40
  const width = Math.min(bubbleRef.value?.$el?.offsetWidth || maxWidth, maxWidth)
  const left = Math.max(edge, Math.min(r.left + r.width / 2 - width / 2, (vw + column) / 2 - width - 12))
  const h = bubbleRef.value?.$el?.offsetHeight || 115
  let placement = 'above', top = r.top - h - 14
  if (top < 88) { placement = 'below'; top = r.bottom + 14 }
  if (top + h > vh - 88) { placement = 'above'; top = Math.max(88, r.top - h - 14) }
  pos.value = { left, top, maxWidth, placement, tailX: Math.max(22, Math.min(r.left + r.width / 2 - left, width - 22)) }
}
function position() {
  cancelAnimationFrame(raf)
  raf = requestAnimationFrame(async () => {
    tick()
    await nextTick()
    // Measure the rendered content before settling the bubble beside its bar.
    if (bubbleRef.value) tick()
  })
}
function onScroll() { emit('close') }
function outside(e) {
  if (!bubbleRef.value?.$el?.contains(e.target) && !props.anchor.contains(e.target)) emit('close')
}
function keydown(e) { if (e.key === 'Escape') emit('close') }
watch(() => [props.anchor, props.label, props.value, props.action], position)
onMounted(() => {
  position()
  window.addEventListener('scroll', onScroll, { capture: true, passive: true })
  window.addEventListener('resize', position)
  window.visualViewport?.addEventListener('resize', position)
  document.addEventListener('pointerdown', outside, true)
  document.addEventListener('keydown', keydown)
  document.addEventListener('focusin', outside)
})
onUnmounted(() => {
  cancelAnimationFrame(raf)
  window.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', position)
  window.visualViewport?.removeEventListener('resize', position)
  document.removeEventListener('pointerdown', outside, true)
  document.removeEventListener('keydown', keydown)
  document.removeEventListener('focusin', outside)
})
</script>

<style scoped>
.chart-day-bubble { position: fixed; z-index: 120; width: max-content; padding-right: 12px; padding-left: 14px; }
.date { margin: 0; padding-right: 18px; font-size: var(--fs-13); font-weight: 400; color: var(--ink-70); }
.value { margin: 3px 0 0; font-size: var(--fs-22); font-weight: 700; line-height: 1.15; font-variant-numeric: tabular-nums; }
.actions { display:flex; justify-content:flex-end; }
.action { display:inline-flex; align-items:flex-end; min-height:44px; }
.action:active .btn-pill { opacity:.6; }
.close { position: absolute; top: 3px; right: 3px; width: 28px; height: 28px; display: grid; place-items: center; color: var(--ink-70); }
</style>
